import { __ } from '@wordpress/i18n';
import { RawHTML, useEffect, useRef } from "@wordpress/element";
import { useBlockProps, InspectorControls, ColorPalette } from '@wordpress/block-editor';
import { DateTimePicker, __experimentalHeading as Heading,  __experimentalUnitControl as UnitControl, PanelBody, ToggleControl, TextControl } from '@wordpress/components';

export const edit = ({ clientId, attributes, setAttributes }) => {
    const { 
        id, 
        hfcCountdownInnerWidth,
        targetTime,
        hfcCountdownLabelColor,
        hfcCountdownLabelSize,
        hfcCountdownShowDays,
        hfcCountdownShowHours,
        hfcCountdownShowMinutes,
        hfcCountdownShowSeconds,
        hfcCountdownHoursLabel,
        hfcCountdownMinutesLabel,
        hfcCountdownSecondsLabel,
        hfcCountdownDaysLabel,
        hfcCountdownShowLabels,
        hfcCountdownShowSeparator
    } = attributes;

    const containerRef = useRef(null);

    /**
     * Set unique ID on initial render.
     */
    useEffect(() => {
        if (!id) {
            const clientIdSliced = clientId.slice(clientId.lastIndexOf("-") + 1);
            setAttributes({ id: 'hfc-cd-timer-' + clientIdSliced });
        }
    }, []);

    /**
     * Set targetTime if not already set.
     */
    useEffect(() => {
        if (!targetTime) {
            const initialTargetTime = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString();
            setAttributes({ targetTime: initialTargetTime });
        }
    }, []);

    useEffect(() => {
        const updateTimer = () => {
            if (containerRef.current) {
                const now = Date.now();
                const target = new Date(targetTime).getTime();
                const remainingTime = Math.max(0, Math.floor((target - now) / 1000));

                const days = Math.floor(remainingTime / (3600 * 24));
                const hours = Math.floor((remainingTime % (3600 * 24)) / 3600);
                const minutes = Math.floor((remainingTime % 3600) / 60);
                const seconds = remainingTime % 60;

                const elements = {
                    days: containerRef.current.querySelector('.days .time-value'),
                    hours: containerRef.current.querySelector('.hours .time-value'),
                    minutes: containerRef.current.querySelector('.minutes .time-value'),
                    seconds: containerRef.current.querySelector('.seconds .time-value'),
                };

                if (elements.days) elements.days.textContent = String(days).padStart(2, '0');
                if (elements.hours) elements.hours.textContent = String(hours).padStart(2, '0');
                if (elements.minutes) elements.minutes.textContent = String(minutes).padStart(2, '0');
                if (elements.seconds) elements.seconds.textContent = String(seconds).padStart(2, '0');
            }
        };

        const interval = setInterval(updateTimer, 1000);
        updateTimer();

        return () => clearInterval(interval);
    }, [targetTime]);

    // Dynamic Styles
    const hfcDynamicStyles = `#${id}.hfc-countdown .hfc-timer-container{
       gap: 24px;
       max-width: ${hfcCountdownInnerWidth};
       margin: 0 auto;
       display: flex;
       align-items: center;
       justify-content: space-between;
    }
    #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box {
       display: flex;
       align-items: center;
       justify-content: space-between;
       flex-direction: column;
       position: relative;
       width: 100%;
    }
    ${!hfcCountdownShowDays ? `#${id}.hfc-countdown .days { display: none; }` : ''}
    ${!hfcCountdownShowHours ? `#${id}.hfc-countdown .hours { display: none; }` : ''}
    ${!hfcCountdownShowMinutes ? `#${id}.hfc-countdown .minutes { display: none; }` : ''}
    ${!hfcCountdownShowSeconds ? `#${id}.hfc-countdown .seconds { display: none; }` : ''}

    ${!hfcCountdownShowHours ? `#${id}.hfc-countdown .hours::after { display: none; }` : ''}
    ${!hfcCountdownShowMinutes ? `#${id}.hfc-countdown .minutes::after { display: none; }` : ''}
    ${!hfcCountdownShowSeconds ? `#${id}.hfc-countdown .minutes::after { display: none; }` : ''}

    ${!hfcCountdownShowSeparator ? ` #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box:not(:last-child)::after{
       display: none;
    }` : ` #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box:not(:last-child)::after{
       position: absolute;
       content: ":";
       color: ${hfcCountdownLabelColor};
       font-size: ${hfcCountdownLabelSize};
       right: -20px;
    }`}
    #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:first-child{
       color: ${hfcCountdownLabelColor};
       font-size: ${hfcCountdownLabelSize};
       font-weight: bold;
       margin: 0px;
    }
    ${!hfcCountdownShowLabels ? `#${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:last-child { display: none; }` : 
    `#${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:last-child{
      margin: 0px;}`
    }
    /* Panel Body CSS */
    .interface-interface-skeleton__body .components-datetime__time-field-time{
       background-color: #fff;
    }
    .interface-interface-skeleton__body .components-base-control__field{
       margin-top: 0px;
    }
    .interface-interface-skeleton__body .components-datetime__time-separator{
       line-height: calc(38px);
    }
    .interface-interface-skeleton__body h3.components-heading{
       margin-bottom: 0px;
    }
    /* Theme Check */
    .editor-styles-wrapper .wp-block-hfc-blocks-countdown-timer{
       max-width: 100%;
    }
    /* Responsive */
    @media (max-width: 781px){
    #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box{
       flex-basis: unset!important;
    }
    }
    @media (max-width: 599px){
     #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:first-child,
     #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box:not(:last-child)::after{
       font-size: 28px;
     }
     #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:last-child{
       font-size: 14px!important;
    }
    }`;

    return (
        <>
            <InspectorControls key={`inspector-controls-${id}`}>
                <PanelBody
                    title={__('Countdown Settings')}
                    icon={'admin-generic'}
                    initialOpen={true}
                    className={'frhd__control frhd__control-query'}
                >
                     <UnitControl
                        label={__('Countdown Inner Width :')}
                        onChange={newValue => setAttributes({ hfcCountdownInnerWidth: newValue })}
                        isUnitSelectTabbable
                        value={hfcCountdownInnerWidth}
                        className={'frhd__component frhd__component-width-90'}
                    />
                    <Heading className={'frhd__pb-heading'}>{__('Select Target Date')}</Heading>
                    <DateTimePicker
                        currentDate={targetTime}
                        onChange={(newDate) => setAttributes({ targetTime: newDate })}
                        is12Hour={true}
                        className={'frhd__component frhd__component-width-90'}
                    />
                     <ToggleControl
                        label={
                            hfcCountdownShowDays
                                ? 'Show Days :'
                                : 'Show Days :'
                        }
                        checked={hfcCountdownShowDays}
                        onChange={newValue => setAttributes({ hfcCountdownShowDays: newValue })}
                        __nextHasNoMarginBottom
                        className={'frhd__component-diff'}
                    />
                     <ToggleControl
                        label={
                            hfcCountdownShowHours
                                ? 'Show Hours :'
                                : 'Show Hours :'
                        }
                        checked={hfcCountdownShowHours}
                        onChange={newValue => setAttributes({ hfcCountdownShowHours: newValue })}
                        __nextHasNoMarginBottom
                        className={'frhd__component-diff'}
                    />
                     <ToggleControl
                        label={
                            hfcCountdownShowMinutes
                                ? 'Show Minutes :'
                                : 'Show Minutes :'
                        }
                        checked={hfcCountdownShowMinutes}
                        onChange={newValue => setAttributes({ hfcCountdownShowMinutes: newValue })}
                        __nextHasNoMarginBottom
                        className={'frhd__component-diff'}
                    />
                     <ToggleControl
                        label={
                            hfcCountdownShowSeconds
                                ? 'Show Seconds :'
                                : 'Show Seconds :'
                        }
                        checked={hfcCountdownShowSeconds}
                        onChange={newValue => setAttributes({ hfcCountdownShowSeconds: newValue })}
                        __nextHasNoMarginBottom
                        className={'frhd__component-diff'}
                    />
                    <Heading className={'frhd__pb-heading'}>Label Color :</Heading>
                    <ColorPalette
                        value={hfcCountdownLabelColor}
                        onChange={(colorValue) => setAttributes({hfcCountdownLabelColor: colorValue })}
                        clearable={false}
                    />
                    <UnitControl
                        label={__('Label Font Size :')}
                        onChange={newValue => setAttributes({ hfcCountdownLabelSize: newValue })}
                        isUnitSelectTabbable
                        value={hfcCountdownLabelSize}
                        className={'frhd__component frhd__component-width-90'}
                    />
                </PanelBody>
                <PanelBody
                    title={__('Visualization')}
                    icon={'admin-generic'}
                    initialOpen={true}
                    className={'frhd__control frhd__control-query'}
                >
                    <ToggleControl
                        label={'Show Labels :'}
                        checked={hfcCountdownShowLabels}
                        onChange={newValue => setAttributes({ hfcCountdownShowLabels: newValue })}
                        __nextHasNoMarginBottom
                        className={'frhd__component-diff'}
                    />
                    {hfcCountdownShowLabels && (
                        <>
                            <TextControl
                                label={__('Seconds Label')}
                                value={hfcCountdownSecondsLabel}
                                onChange={newValue => setAttributes({ hfcCountdownSecondsLabel: newValue })}
                                __nextHasNoMarginBottom
                                className={'frhd__component-diff'}
                            />
                            <TextControl
                                label={__('Minutes Label')}
                                value={hfcCountdownMinutesLabel}
                                onChange={newValue => setAttributes({ hfcCountdownMinutesLabel: newValue })}
                                __nextHasNoMarginBottom
                                className={'frhd__component-diff'}
                            />
                            <TextControl
                                label={__('Hours Label')}
                                value={hfcCountdownHoursLabel}
                                onChange={newValue => setAttributes({ hfcCountdownHoursLabel: newValue })}
                                __nextHasNoMarginBottom
                                className={'frhd__component-diff'}
                            />
                            <TextControl
                                label={__('Days Label')}
                                value={hfcCountdownDaysLabel}
                                onChange={newValue => setAttributes({ hfcCountdownDaysLabel: newValue })}
                                __nextHasNoMarginBottom
                                className={'frhd__component-diff'}
                            />
                        </>
                    )}
        <ToggleControl
            label={'Show Separator :'}
            checked={hfcCountdownShowSeparator}
            onChange={newValue => setAttributes({ hfcCountdownShowSeparator: newValue })}
            __nextHasNoMarginBottom
            className={'frhd__component-diff'}
        />
              </PanelBody>
            </InspectorControls>
            <div {...useBlockProps()}>
            <RawHTML>{`<style>${hfcDynamicStyles}</style>`}</RawHTML>
               <div id={id} className='hfc-countdown' ref={containerRef}>
               <div className="hfc-timer-container">
                    {hfcCountdownShowDays && (
                        <div className="hfc-timer-box days">
                            <p className="time-value">00</p>
                            <p className="time-label">{hfcCountdownDaysLabel || 'DAYS'}</p>
                        </div>
                    )}
                    {hfcCountdownShowHours && (
                        <div className="hfc-timer-box hours">
                            <p className="time-value">00</p>
                            <p className="time-label">{hfcCountdownHoursLabel || 'HRS'}</p>
                        </div>
                    )}
                    {hfcCountdownShowMinutes && (
                        <div className="hfc-timer-box minutes">
                            <p className="time-value">00</p>
                            <p className="time-label">{hfcCountdownMinutesLabel || 'MINS'}</p>
                        </div>
                    )}
                    {hfcCountdownShowSeconds && (
                        <div className="hfc-timer-box seconds">
                            <p className="time-value">00</p>
                            <p className="time-label">{hfcCountdownSecondsLabel || 'SECS'}</p>
                        </div>
                    )}
                </div>
               </div>
            </div>
        </>
    );
};

export const save = ({ attributes }) => {
    const { 
        id, 
        hfcCountdownInnerWidth,
        targetTime,
        hfcCountdownLabelColor,
        hfcCountdownLabelSize,
        hfcCountdownShowDays,
        hfcCountdownShowHours,
        hfcCountdownShowMinutes,
        hfcCountdownShowSeconds,
        hfcCountdownHoursLabel,
        hfcCountdownMinutesLabel,
        hfcCountdownSecondsLabel,
        hfcCountdownDaysLabel,
        hfcCountdownShowLabels,
        hfcCountdownShowSeparator
    } = attributes;

    const countdownScript = `
        document.addEventListener("DOMContentLoaded", function () {
            const countdownElement = document.getElementById('${id}');
            if (!countdownElement) return;

            function updateTimer() {
                const currentTime = Date.now();
                const targetTimestamp = new Date('${targetTime}').getTime();
                const remainingTime = Math.max(0, Math.floor((targetTimestamp - currentTime) / 1000));

                const days = Math.floor(remainingTime / (3600 * 24));
                const hours = Math.floor((remainingTime % (3600 * 24)) / 3600);
                const minutes = Math.floor((remainingTime % 3600) / 60);
                const seconds = remainingTime % 60;

                countdownElement.querySelector('.days').innerText = String(days).padStart(2, '0');
                countdownElement.querySelector('.hours').innerText = String(hours).padStart(2, '0');
                countdownElement.querySelector('.minutes').innerText = String(minutes).padStart(2, '0');
                countdownElement.querySelector('.seconds').innerText = String(seconds).padStart(2, '0');
            }

            updateTimer();
            setInterval(updateTimer, 1000);
        });
    `;

    const hfcDynamicStyles = `#${id}.hfc-countdown .hfc-timer-container{
        gap: 24px;
        max-width: ${hfcCountdownInnerWidth};
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
     }
     #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-direction: column;
        position: relative;
        width: 100%;
    }
    ${!hfcCountdownShowDays ? `#${id}.hfc-countdown .days { display: none; }` : ''}
    ${!hfcCountdownShowHours ? `#${id}.hfc-countdown .hours { display: none; }` : ''}
    ${!hfcCountdownShowMinutes ? `#${id}.hfc-countdown .minutes { display: none; }` : ''}
    ${!hfcCountdownShowSeconds ? `#${id}.hfc-countdown .seconds { display: none; }` : ''}

    ${!hfcCountdownShowHours ? `#${id}.hfc-countdown .hours::after { display: none; }` : ''}
    ${!hfcCountdownShowMinutes ? `#${id}.hfc-countdown .minutes::after { display: none; }` : ''}
    ${!hfcCountdownShowSeconds ? `#${id}.hfc-countdown .minutes::after { display: none; }` : ''}

    ${!hfcCountdownShowSeparator ? ` #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box:not(:last-child)::after{
       display: none;
    }` : ` #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box:not(:last-child)::after{
       position: absolute;
       content: ":";
       color: ${hfcCountdownLabelColor};
       font-size: ${hfcCountdownLabelSize};
       right: -20px;
    }`}
    #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:first-child{
       color: ${hfcCountdownLabelColor};
       font-size: ${hfcCountdownLabelSize};
       font-weight: bold;
       margin: 0px;
    }
    ${!hfcCountdownShowLabels ? `#${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:last-child { display: none; }` : 
        `#${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:last-child{
          margin: 0px;}`
    }
      /* Responsive */
    @media (max-width: 781px){
    #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box{
       flex-basis: unset!important;
    }
    }
    @media (max-width: 599px){
     #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:first-child,
     #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box:not(:last-child)::after{
       font-size: 28px;
     }
    #${id}.hfc-countdown .hfc-timer-container .hfc-timer-box p:last-child{
       font-size: 14px!important;
    }
    }`;

    return (
        <>
            <RawHTML>{`<style>${hfcDynamicStyles}</style>`}</RawHTML>
            <div id={id} className={'hfc-countdown'}>
                <div className="hfc-timer-container">
                    {hfcCountdownShowDays && (
                        <div className="hfc-timer-box">
                            <p className="days">00</p>
                            <p className="time-label">{hfcCountdownDaysLabel || 'DAYS'}</p>
                        </div>
                    )}
                    {hfcCountdownShowHours && (
                        <div className="hfc-timer-box">
                            <p className="hours">00</p>
                            <p className="time-label">{hfcCountdownHoursLabel || 'HRS'}</p>
                        </div>
                    )}
                    {hfcCountdownShowMinutes && (
                        <div className="hfc-timer-box">
                            <p className="minutes">00</p>
                            <p className="time-label">{hfcCountdownMinutesLabel || 'MINS'}</p>
                        </div>
                    )}
                    {hfcCountdownShowSeconds && (
                        <div className="hfc-timer-box">
                            <p className="seconds">00</p>
                            <p className="time-label">{hfcCountdownSecondsLabel || 'SECS'}</p>
                        </div>
                    )}
                </div>
            </div>
            <RawHTML>{`<script>${countdownScript}</script>`}</RawHTML>
       </>
    );
};